const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

async function bookingFetch(path, options = {}) {
  const token = localStorage.getItem('developmentexpress_token');
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token || ''}`,
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || data.error || 'Request failed');
    err.status = res.status;
    err.code = data.code;
    err.data = data.data || data;
    throw err;
  }
  return data;
}

export const requestBookingExtension = (bookingId, requestedHours) =>
  bookingFetch(`/api/bookings/${encodeURIComponent(bookingId)}/extensions`, {
    method: 'POST',
    body: JSON.stringify({ requested_hours: requestedHours }),
  });

export const confirmBookingWork = (bookingId) =>
  bookingFetch(`/api/bookings/${encodeURIComponent(bookingId)}/client-confirm`, {
    method: 'POST',
    body: JSON.stringify({}),
  });

export const disputeBookingWork = (bookingId, reason) =>
  bookingFetch(`/api/bookings/${encodeURIComponent(bookingId)}/dispute`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });

export const operatorCompleteBooking = (bookingId, actualHours, notes) =>
  bookingFetch(`/api/bookings/${encodeURIComponent(bookingId)}/operator-complete`, {
    method: 'POST',
    body: JSON.stringify({ actual_hours: actualHours, notes }),
  });

export const adminResolveDispute = (bookingId, overrideHours, reason) =>
  bookingFetch(`/api/admin/bookings/${encodeURIComponent(bookingId)}/resolve-dispute`, {
    method: 'POST',
    body: JSON.stringify({ override_hours: overrideHours, reason }),
  });

export const adminForceSettle = (bookingId, { paymentMethod, adminOverrideHours } = {}) =>
  bookingFetch(`/api/admin/bookings/${encodeURIComponent(bookingId)}/force-settle`, {
    method: 'POST',
    body: JSON.stringify({
      payment_method: paymentMethod,
      admin_override_hours: adminOverrideHours != null && adminOverrideHours !== ''
        ? Number(adminOverrideHours)
        : undefined,
    }),
  });

export const adminForceCancel = (bookingId, reason) =>
  bookingFetch(`/api/admin/bookings/${encodeURIComponent(bookingId)}/force-cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });

export const adminStartBooking = (bookingId) =>
  bookingFetch(`/api/bookings/${encodeURIComponent(bookingId)}/start`, {
    method: 'POST',
    body: JSON.stringify({}),
  });

export function estimateBalanceDue(booking) {
  const minH = Number(booking.minimum_hours || 3);
  const hours = Math.max(
    Number(booking.admin_override_hours ??
      booking.operator_actual_hours ??
      booking.booked_hours ?? minH),
    minH,
  );
  const rate = Number(booking.rate_per_hour || 0);
  const base = rate > 0
    ? Math.round(hours * rate * 100) / 100
    : Number(booking.base_amount ||
        booking.total_amount || 0);

  // Apply original GST rate if booking
  // had GST (B2B client)
  const origBase = Number(booking.base_amount || 0);
  const origGst = Number(booking.gst_amount || 0);
  const gstRate = origBase > 0
    ? origGst / origBase
    : 0.18;
  const gst = gstRate > 0
    ? Math.round(base * gstRate * 100) / 100
    : 0;
  const gross = Math.round((base + gst) * 100) / 100;

  // Subtract advance + extension advances
  const advance = Number(booking.advance_paid || 0);
  const extAdv = Number(
    booking.extension_advances_sum || 0
  );
  return Math.max(0,
    Math.round((gross - advance - extAdv) * 100) / 100
  );
}

export function runningElapsedMs(booking, nowMs = Date.now()) {
  if (!booking?.machine_started_at) return 0;
  const started = new Date(booking.machine_started_at).getTime();
  if (!Number.isFinite(started)) return 0;
  const pausedMs = booking.pause_started_at
    ? (nowMs - new Date(booking.pause_started_at).getTime())
      + Number(booking.total_paused_ms || 0)
    : Number(booking.total_paused_ms || 0);
  return Math.max(0, nowMs - started - pausedMs);
}

export function isBookingPaused(booking) {
  return Boolean(booking?.pause_started_at);
}

/** Active machine timer — poll client/owner dashboards to sync pause state. */
export function bookingNeedsLiveSync(booking) {
  if (!booking?.machine_started_at) return false;
  const status = String(booking.status || '');
  return status === 'Dispatched' || status === 'Extension_Pending';
}

function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

export function formatClockHMS(ms) {
  const totalSec = Math.max(0, Math.floor(Number(ms) / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export function msToHmsParts(ms) {
  const totalSec = Math.max(0, Math.floor(Number(ms) / 1000));
  return {
    h: Math.floor(totalSec / 3600),
    m: Math.floor((totalSec % 3600) / 60),
    s: totalSec % 60,
  };
}

export function hmsPartsToMs(h, m, s) {
  const hh = Math.max(0, Number(h) || 0);
  const mm = Math.min(59, Math.max(0, Number(m) || 0));
  const ss = Math.min(59, Math.max(0, Number(s) || 0));
  return ((hh * 3600) + (mm * 60) + ss) * 1000;
}

export function hmsPartsToDecimalHours(h, m, s) {
  return round2(hmsPartsToMs(h, m, s) / 3600000);
}

export function decimalHoursToHMS(decimalHrs) {
  const totalSec = Math.round(
    Number(decimalHrs || 0) * 3600
  );
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

/** Fill billing fields from joined machines row when booking columns are null. */
export function enrichBookingFromJoin(booking) {
  if (!booking) return booking;
  const machine = booking.machines || {};
  return {
    ...booking,
    rate_per_hour: booking.rate_per_hour ?? machine.rate_per_hour ?? null,
    minimum_hours: booking.minimum_hours ?? machine.minimum_hours ?? 3,
  };
}

export function formatSignedHmsDiff(diffMs) {
  const n = Number(diffMs) || 0;
  if (n === 0) return '0:00:00';
  const sign = n > 0 ? '+' : '-';
  return `${sign}${formatClockHMS(Math.abs(n))}`;
}

export function formatInrDisplay(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Live running gross + outstanding (display only; no wallet debit). */
export function estimateLiveMeter(booking) {
  const minH = Number(booking?.minimum_hours || 3);
  const elapsedMs = runningElapsedMs(booking);
  const elapsedH = elapsedMs / 3600000;
  const rate = Number(booking?.rate_per_hour || 0);
  const advance = Number(booking?.advance_paid || 0);

  if (!Number.isFinite(rate) || rate <= 0) {
    const gross = Number(booking?.total_amount || 0);
    const safeGross = Number.isFinite(gross) ? gross : 0;
    return {
      gross: round2(safeGross),
      outstanding: Math.max(0, round2(safeGross - advance)),
      minimumApplied: false,
      hasHourlyRate: false,
    };
  }

  const runningGross = round2(elapsedH * rate);
  const minimumGross = round2(minH * rate);
  const gross = Math.max(runningGross, minimumGross);

  return {
    gross,
    outstanding: Math.max(0, round2(gross - advance)),
    minimumApplied: elapsedH < minH,
    hasHourlyRate: true,
    minH,
    rate,
  };
}

export function formatDuration(ms) {
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h} hr ${m} min`;
}

export function confirmDeadlineRemaining(booking) {
  if (!booking.client_confirm_deadline) return null;
  return Math.max(0, new Date(booking.client_confirm_deadline).getTime() - Date.now());
}
